/**
 * paymentUtils.ts
 *
 * Utility functions for payment processing
 * Handles amount formatting, validation, and dynamic expression evaluation
 */

/**
 * Format amount as currency string
 * @param amount Amount in cents or smallest currency unit
 * @param currency Currency code (default: 'ZAR')
 * @returns Formatted currency string
 */
export function formatCurrency(amount: number, currency: string = 'ZAR'): string {
  const currencySymbols: Record<string, string> = {
    ZAR: 'R',
    USD: '$',
    EUR: '€',
    GBP: '£',
  };

  const symbol = currencySymbols[currency] || currency;
  const formattedAmount = (amount / 100).toFixed(2);

  return `${symbol}${formattedAmount}`;
}

/**
 * Validate payment amount is within acceptable range
 * @param amount Amount in cents
 * @returns Validation result
 */
export function validatePaymentAmount(
  amount: number
): { valid: boolean; error?: string } {
  if (typeof amount !== 'number' || isNaN(amount)) {
    return { valid: false, error: 'Amount must be a valid number' };
  }

  if (amount <= 0) {
    return { valid: false, error: 'Amount must be greater than 0' };
  }

  // Maximum amount: R999,999.99 (99999999 cents)
  if (amount > 99999999) {
    return { valid: false, error: 'Amount exceeds maximum allowed (R999,999.99)' };
  }

  return { valid: true };
}

/**
 * Extract field references from dynamic expression
 * Example: "{price} + {tax}" -> ["price", "tax"]
 * @param expression Dynamic expression with {fieldDataKey} placeholders
 * @returns Array of field data keys referenced in expression
 */
export function extractFieldReferences(expression: string): string[] {
  if (!expression) return [];

  const fieldRefPattern = /\{([^}]+)\}/g;
  const references: string[] = [];
  let match;

  while ((match = fieldRefPattern.exec(expression)) !== null) {
    const fieldDataKey = match[1].trim();
    if (fieldDataKey && !references.includes(fieldDataKey)) {
      references.push(fieldDataKey);
    }
  }

  return references;
}

/**
 * Safely evaluate dynamic expression with form values
 * Example: "{price} * {quantity}" with {price: 100, quantity: 2} -> 200
 * @param expression Dynamic expression with {fieldDataKey} placeholders
 * @param formValues Form values indexed by dataKey
 * @returns Evaluated result in cents, or error
 */
export function evaluateDynamicExpression(
  expression: string,
  formValues: Record<string, any>
): { result: number; error?: string } {
  try {
    if (!expression || expression.trim() === '') {
      return { result: 0, error: 'Expression cannot be empty' };
    }

    // Extract all field references
    const fieldRefPattern = /\{([^}]+)\}/g;
    const references: string[] = [];
    let match;

    while ((match = fieldRefPattern.exec(expression)) !== null) {
      const fieldDataKey = match[1].trim();
      if (fieldDataKey) {
        references.push(fieldDataKey);
      }
    }

    // Validate all referenced fields exist and have numeric values
    for (const ref of references) {
      if (!(ref in formValues)) {
        return { result: 0, error: `Field "${ref}" not found in form values` };
      }

      const value = formValues[ref];
      const numValue = parseFloat(value);

      if (isNaN(numValue)) {
        return {
          result: 0,
          error: `Field "${ref}" contains non-numeric value: "${value}"`,
        };
      }
    }

    // Replace field references with actual values
    let evaluableExpr = expression;
    for (const ref of references) {
      const value = formValues[ref];
      const numValue = parseFloat(value);
      evaluableExpr = evaluableExpr.replace(
        new RegExp(`\\{${ref}\\}`, 'g'),
        numValue.toString()
      );
    }

    // Validate expression only contains numbers and allowed operators
    const allowedPattern = /^[\d\s+\-*/.()]+$/;
    if (!allowedPattern.test(evaluableExpr)) {
      return {
        result: 0,
        error: 'Expression contains invalid characters. Only numbers and +, -, *, /, (, ) are allowed.',
      };
    }

    // Evaluate expression safely using Function constructor
    // This is safer than eval() but still requires validation
    const fn = new Function('return ' + evaluableExpr);
    const result = fn();

    // Validate result
    if (typeof result !== 'number' || isNaN(result)) {
      return { result: 0, error: 'Expression did not evaluate to a valid number' };
    }

    if (result < 0) {
      return { result: 0, error: 'Amount cannot be negative' };
    }

    if (result > 999999.99) {
      return {
        result: 0,
        error: 'Calculated amount exceeds maximum allowed (R999,999.99)',
      };
    }

    // Convert to cents and round to nearest cent
    const amountInCents = Math.round(result * 100);

    return { result: amountInCents };
  } catch (e) {
    return {
      result: 0,
      error: `Invalid expression: ${(e as Error).message}`,
    };
  }
}

/**
 * Generate unique payment reference
 * @param formId Form ID
 * @param fieldId Field ID
 * @param sessionId Optional session ID
 * @returns Unique payment reference
 */
export function generatePaymentReference(
  formId: string,
  fieldId: string,
  sessionId?: string
): string {
  const timestamp = Date.now();
  const random = Math.random().toString(36).substring(2, 8);
  const parts = [
    'PAY',
    formId.substring(0, 8),
    fieldId.substring(0, 8),
    sessionId?.substring(0, 8) || 'NOSESS',
    timestamp.toString(36),
    random,
  ];

  return parts.join('-').toUpperCase();
}

/**
 * Sanitize metadata for payment providers
 * Removes nested objects and ensures all values are strings or numbers
 * @param data Metadata object
 * @param maxDepth Maximum depth to traverse (default: 1)
 * @returns Sanitized metadata
 */
export function sanitizePaymentMetadata(
  data: Record<string, any>,
  maxDepth: number = 1
): Record<string, string | number> {
  const sanitized: Record<string, string | number> = {};

  for (const [key, value] of Object.entries(data)) {
    if (value === null || value === undefined) {
      continue;
    }

    if (typeof value === 'string' || typeof value === 'number') {
      sanitized[key] = value;
    } else if (typeof value === 'boolean') {
      sanitized[key] = value ? 'true' : 'false';
    } else if (typeof value === 'object' && maxDepth > 0) {
      // Flatten nested objects with dot notation
      const nested = sanitizePaymentMetadata(value, maxDepth - 1);
      for (const [nestedKey, nestedValue] of Object.entries(nested)) {
        sanitized[`${key}.${nestedKey}`] = nestedValue;
      }
    } else {
      // Convert everything else to string
      sanitized[key] = String(value);
    }
  }

  return sanitized;
}

/**
 * Convert amount from user input (decimal) to cents
 * @param amount Amount in decimal format (e.g., 100.50)
 * @returns Amount in cents
 */
export function amountToCents(amount: number): number {
  return Math.round(amount * 100);
}

/**
 * Convert amount from cents to decimal
 * @param amountInCents Amount in cents
 * @returns Amount in decimal format
 */
export function centsToAmount(amountInCents: number): number {
  return amountInCents / 100;
}
